import $ from 'jquery';
import { isNil } from 'lodash';
import { sanitizeHtml } from './utils';

const DEFAULT_DELAY_MS = 8000;

type AlertClass = 'danger' | 'warning' | 'success' | 'info';

// Flash messages for AJAX actions.
$(document).ajaxComplete(function (event, request) {
  var msg: string | null;
  var delay: number | null =
    request.getResponseHeader('X-Message-Keep') === 'true' ? -1 : null;
  var showAfterTurbolinkLoad =
    request.getResponseHeader('X-Message-AfterTurbolinkLoad') === 'true';

  if ((msg = request.getResponseHeader('X-Message-Alert'))) {
    showAlertMessage(decodeURIComponent(msg), delay, showAfterTurbolinkLoad);
  }

  if ((msg = request.getResponseHeader('X-Message-Warning'))) {
    showWarningMessage(decodeURIComponent(msg), delay, showAfterTurbolinkLoad);
  }

  if ((msg = request.getResponseHeader('X-Message-Notice'))) {
    showNoticeMessage(decodeURIComponent(msg), delay, showAfterTurbolinkLoad);
  }

  if ((msg = request.getResponseHeader('X-Message-Alert-HTML'))) {
    dangerouslyShowAlertMessage(
      decodeURIComponent(msg),
      delay,
      showAfterTurbolinkLoad
    );
  }

  if ((msg = request.getResponseHeader('X-Message-Warning-HTML'))) {
    dangerouslyShowWarningMessage(
      decodeURIComponent(msg),
      delay,
      showAfterTurbolinkLoad
    );
  }

  if ((msg = request.getResponseHeader('X-Message-Notice-HTML'))) {
    dangerouslyShowNoticeMessage(
      decodeURIComponent(msg),
      delay,
      showAfterTurbolinkLoad
    );
  }

  if ((msg = request.getResponseHeader('X-Message-Info'))) {
    showInfoMessage(decodeURIComponent(msg), delay, showAfterTurbolinkLoad);
  }

  if ((msg = request.getResponseHeader('X-Message-Info-HTML'))) {
    dangerouslyShowInfoMessage(
      decodeURIComponent(msg),
      delay,
      showAfterTurbolinkLoad
    );
  }
});

// Allow queuing of flash messages after a turoblink page loads
const messagesToShowAfterTurbolinkLoad: Array<() => void> = [];

$(document).on('page:load', function () {
  while (messagesToShowAfterTurbolinkLoad.length !== 0) {
    const showMessageFn = messagesToShowAfterTurbolinkLoad.shift();
    if (isNil(showMessageFn)) continue;
    showMessageFn();
  }
});

// Set message as text (no escaping required)
function showAlertMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, false, 'danger', showAfterTurbolinkLoad);
}

function showWarningMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, false, 'warning', showAfterTurbolinkLoad);
}

function showNoticeMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, false, 'success', showAfterTurbolinkLoad);
}

function showInfoMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, false, 'info', showAfterTurbolinkLoad);
}

// Show message as HTML (must escape user input to avoid XSS)
function dangerouslyShowAlertMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, true, 'danger', showAfterTurbolinkLoad);
}

function dangerouslyShowWarningMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(msg, delayMS, true, 'warning', showAfterTurbolinkLoad);
}

function dangerouslyShowNoticeMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(
    msg,
    delayMS ?? DEFAULT_DELAY_MS,
    true,
    'success',
    showAfterTurbolinkLoad
  );
}

function dangerouslyShowInfoMessage(
  msg: string,
  delayMS?: number | null,
  showAfterTurbolinkLoad?: boolean
) {
  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }
  showUserMessage(
    msg,
    delayMS ?? DEFAULT_DELAY_MS,
    true,
    'info',
    showAfterTurbolinkLoad
  );
}

function showUserMessage(
  msg: string,
  delayMS: number | null | undefined,
  htmlSafe: boolean,
  alertClass: AlertClass,
  showAfterTurbolinkLoad?: boolean
) {
  let delayOrDefault = delayMS ?? DEFAULT_DELAY_MS;

  if (showAfterTurbolinkLoad == null) {
    showAfterTurbolinkLoad = false;
  }

  // DO NOT REPLICATE: this would be unsafe if alertClass were user controllable.
  const flash = $(`
    <aha-alert type="${alertClass}" dismissable>
      <button type="button" slot="close">
        <i class="fa-regular fa-times"></i>
      </button>
    </aha-alert>
  `);
  const div = $('<div></div>');
  if (htmlSafe === true) {
    // We explicitly opt-in to HTML inserting behavior.
    // eslint-disable-next-line no-unsanitized/method
    div.html(msg);
  } else {
    div.text(msg);
  }
  // eslint-disable-next-line no-unsanitized/method
  flash.append(div);

  const showMessageFn = () => {
    // Make flash messages not block any content
    if (window.railsEnv === 'test') {
      // Never hide the messages, so we can run assertions against them
      delayOrDefault = -1;
    }

    // If there is already a warning message with the same text then replace it
    // with this one so the timeout gets reset and the user doesn't see a stack
    // of messages
    const duplicateWarnings = $('.ajax-flash')
      .children('.alert, aha-alert')
      .filter((_, el) => {
        return alertClass === 'warning' && $(el).text() === flash.text();
      });

    if (duplicateWarnings.length > 0) {
      duplicateWarnings.hide();
      flash.insertBefore(duplicateWarnings.first());
    } else {
      flash.appendTo('.ajax-flash');
    }
    showPopover(flash);

    if (delayOrDefault !== -1) {
      return flash.delay(delayOrDefault).fadeOut(300, 'swing', function () {
        return $(this).remove();
      });
    }
  };

  if (showAfterTurbolinkLoad) {
    messagesToShowAfterTurbolinkLoad.push(showMessageFn);
  } else {
    showMessageFn();
  }
}

// Subscribe to asynchronous per-user messages.
function subscribeToAsyncMessages(client) {
  var userId = $('meta[name=user-id]').attr('content');
  var accountId = $('meta[name=account-id]').attr('content');

  client.subscribe(
    `/messages/listen/user-${userId}/account-${accountId}`,
    function (message) {
      if (message.level === 'notice') {
        message.level = 'success';
      }
      showUserMessage(message.content, 20000, message.html, message.level);
    }
  );
}

function showReactorRefreshButton() {
  // Don't show more than once.
  if ($('.reactor-refresh-message').length > 0) return;

  // See if the page has place for the button to go.
  const row = $(
    '#page-nav .page-nav__row:not(.reactor-no-refresh-message)'
  ).first();
  if (row.length === 1) {
    const cell = $(row)
      .find('.page-nav__cell.page-nav__cell--justify-right')
      .first();
    const buttonTemplate = `<div class='page-nav__cell page-nav__cell--justify-right'>
        <a class='reactor-refresh-message' href=''><i class='fa-solid fa-arrows-rotate'></i>Refresh to get the latest</a>
      </div>`;

    if (cell.length === 1) {
      cell.before(sanitizeHtml(buttonTemplate));
    } else {
      row.append(sanitizeHtml(buttonTemplate));
    }
    $('.reactor-refresh-message').on('click', function () {
      $(this).find('i').addClass('fa-spin');
    });
  }
}

function showPopover($node) {
  const flashContainer = $node[0].closest('.ajax-flash');
  if (flashContainer instanceof HTMLElement) {
    if (!flashContainer.hasAttribute('popover')) {
      flashContainer.setAttribute('popover', 'manual');
    }
    if (flashContainer.matches(':popover-open')) {
      flashContainer.hidePopover();
    }
    flashContainer.showPopover();
  }
}

$(window).on('faye:disconnect', showReactorRefreshButton);

export {
  showAlertMessage,
  showWarningMessage,
  showNoticeMessage,
  showInfoMessage,
  dangerouslyShowAlertMessage,
  dangerouslyShowWarningMessage,
  dangerouslyShowNoticeMessage,
  dangerouslyShowInfoMessage,
  subscribeToAsyncMessages,
};
